/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import moment from 'moment'
import { renderEventCardPreview } from './event_card_preview'
import { NO_TITLE_FALLBACK } from './calendar_utils'
import { calendarTheme } from './calendar_theme'

const MAX_VISIBLE_OCCURRENCES = 3

export const MonthGrid = ({ monthStart, monthDays, occurrencesByDate, onOpenOccurrence, onDrillToWeek }) => {
  const weekdayLabels = useMemo(() => monthDays.slice(0, 7).map((day) => day.format('ddd')), [monthDays])

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          minWidth: 980,
          borderTop: `1px solid ${calendarTheme.colors.gridBorder}`,
          borderLeft: `1px solid ${calendarTheme.colors.gridBorder}`,
          backgroundColor: calendarTheme.colors.background,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderBottom: `1px solid ${calendarTheme.colors.gridBorder}`,
          }}
        >
          {weekdayLabels.map((label, index) => (
            <Box
              key={`weekday-${index}`}
              sx={{
                borderRight: `1px solid ${calendarTheme.colors.gridBorder}`,
                px: 1.5,
                py: 1.25,
                textAlign: 'center',
                boxSizing: 'border-box',
              }}
            >
              <Typography sx={{ fontSize: '12px', color: calendarTheme.colors.mutedText, fontWeight: 600 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {monthDays.map((day) => {
            const dayKey = day.format('YYYY-MM-DD')
            const isCurrentMonth = day.isSame(monthStart, 'month')
            const isToday = day.isSame(moment(), 'day')
            const dayOccurrences = occurrencesByDate?.[dayKey] || []
            const visibleOccurrences = dayOccurrences.slice(0, MAX_VISIBLE_OCCURRENCES)
            const overflowCount = dayOccurrences.length - visibleOccurrences.length

            return (
              <Box
                key={dayKey}
                sx={{
                  minHeight: 120,
                  borderRight: `1px solid ${calendarTheme.colors.gridBorder}`,
                  borderBottom: `1px solid ${calendarTheme.colors.gridBorder}`,
                  backgroundColor: isCurrentMonth ? calendarTheme.colors.background : '#fafafa',
                  boxSizing: 'border-box',
                  px: 1,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => onDrillToWeek(day)}
                  data-testid={`dynamic-discounts-calendar-month-day-${dayKey}`}
                  aria-label={`View week of ${day.format('MMMM D, YYYY')}`}
                  sx={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                    padding: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isToday ? calendarTheme.defaultCardColor : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: isToday
                        ? calendarTheme.colors.eventBackground
                        : isCurrentMonth
                          ? calendarTheme.colors.headingText
                          : calendarTheme.colors.mutedText,
                    }}
                  >
                    {day.format('D')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {visibleOccurrences.map((occurrence) => (
                    <Box
                      key={occurrence.id}
                      role="button"
                      tabIndex={0}
                      data-testid={`dynamic-discounts-calendar-month-event-${occurrence.id}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenOccurrence(occurrence)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          event.stopPropagation()
                          onOpenOccurrence(occurrence)
                        }
                      }}
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: calendarTheme.colors.eventBackground,
                        border: `1px solid ${calendarTheme.defaultCardColor}`,
                        px: 1,
                        py: 0.75,
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.2 }} noWrap>
                        {occurrence.title || NO_TITLE_FALLBACK}
                      </Typography>
                      {occurrence.slotLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.25 }}>
                          <Typography sx={{ fontSize: '10px', color: calendarTheme.colors.mutedText, lineHeight: 1.2, minWidth: 0, flex: '1 1 auto' }} noWrap>
                            {occurrence.slotLabel}
                          </Typography>
                          <Box data-testid={`dynamic-discounts-calendar-month-event-preview-${occurrence.id}`} sx={{ flexShrink: 0 }}>
                            {renderEventCardPreview(occurrence.blockedCards, 'inline-compact')}
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  ))}

                  {overflowCount > 0 ? (
                    <Box
                      component="button"
                      type="button"
                      data-testid={`dynamic-discounts-calendar-month-more-${dayKey}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        onDrillToWeek(day)
                      }}
                      sx={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        padding: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: calendarTheme.colors.mutedText }}>
                        +{overflowCount} more
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
